import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ModeSelector, { MODE_REM_ORDER, MODE_STORE_LOCATION, MODE_MANUAL } from './components/ModeSelector'
import RemOrderInput from './components/RemOrderInput'
import StoreLocationInput from './components/StoreLocationInput'
import ManualInput from './components/ManualInput'
import MovementParams from './components/MovementParams'
import PreviewTable from './components/PreviewTable'
import ResultTable from './components/ResultTable'
import { fetchOptions, previewDepositTransfer, executeDepositTransfer, fetchExecuteTaskStatus } from '../../../services/naturaDepositTransferService'

// A worker-run batch is one SAP call per lot at up to 10s each, so polling much
// faster than this only adds requests without telling the operator anything new.
const EXECUTE_POLL_INTERVAL_MS = 3000

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms))

const STATE_IDLE = 'idle'
const STATE_PREVIEWING = 'previewing'
const STATE_PREVIEW_READY = 'preview_ready'
const STATE_CONFIRMING = 'confirming'
const STATE_DONE = 'done'

const ERROR_NONE = null
const ERROR_INLINE = 'inline'
const ERROR_GENERIC = 'generic'

const isNumeric = v => typeof v === 'string' && v.trim() !== '' && /^\d+$/.test(v.trim())
const isPositiveInt = v => isNumeric(v) && parseInt(v, 10) > 0
const isNonEmpty = v => typeof v === 'string' && v.trim() !== ''

export default function DepositTransferPage() {
  const { t } = useTranslation()

  const [mode, setMode] = useState(MODE_REM_ORDER)
  const [inputs, setInputs] = useState({})
  const [params, setParams] = useState({})
  const [options, setOptions] = useState({})

  const [state, setState] = useState(STATE_IDLE)
  const [preview, setPreview] = useState(null)
  const [finalize, setFinalize] = useState(false)
  const [result, setResult] = useState(null)

  const [progressPct, setProgressPct] = useState(null)

  // Guards the poll loop, which can outlast the page.
  const mountedRef = useRef(true)
  useEffect(
    () => () => {
      mountedRef.current = false
    },
    [],
  )

  const [errorKind, setErrorKind] = useState(ERROR_NONE)
  const [inlineError, setInlineError] = useState(null)
  const [optionsLoadFailed, setOptionsLoadFailed] = useState(false)

  useEffect(() => {
    let active = true
    fetchOptions()
      .then(data => {
        if (!active) return
        setOptions(data || {})
        setOptionsLoadFailed(false)
      })
      .catch(() => {
        if (!active) return
        // Page is non-functional without move types / deposits — surface it.
        setOptionsLoadFailed(true)
      })
    return () => {
      active = false
    }
  }, [])

  // Stale-preview guard: any edit to step-1 or step-2 invalidates the preview
  // and clears step 3 / results so a stale preview can't be confirmed.
  const invalidatePreview = () => {
    setPreview(null)
    setResult(null)
    setErrorKind(ERROR_NONE)
    setInlineError(null)
    setState(STATE_IDLE)
  }

  const handleModeChange = next => {
    setMode(next)
    setInputs({})
    invalidatePreview()
  }

  const handleInputsChange = patch => {
    setInputs(prev => ({ ...prev, ...patch }))
    invalidatePreview()
  }

  const handleParamsChange = patch => {
    setParams(prev => ({ ...prev, ...patch }))
    invalidatePreview()
  }

  const buildPreviewPayload = () => ({ mode, ...inputs })

  const generatePreview = async () => {
    if (state === STATE_PREVIEWING) return
    setState(STATE_PREVIEWING)
    setPreview(null)
    setResult(null)
    setErrorKind(ERROR_NONE)
    setInlineError(null)
    try {
      const data = await previewDepositTransfer(buildPreviewPayload())
      setPreview(data)
      setState(STATE_PREVIEW_READY)
    } catch (e) {
      handleError(e)
      setState(STATE_IDLE)
    }
  }

  // A finished batch always carries `summary`. Rendering anything else shows a zeroed
  // summary that reads as "nothing happened" while SAP already has the movements.
  const showResult = data => {
    if (!data?.summary) {
      setInlineError(t('natura_operations.deposit_transfer.result_unreadable'))
      setErrorKind(ERROR_INLINE)
      setState(STATE_DONE)
      return
    }
    setResult(data)
    setState(STATE_DONE)
  }

  const confirm = async () => {
    // STATE_DONE included: the batch for this preview already reached SAP, and
    // WP16 is not idempotent — a second send re-reports every lot that still has stock.
    if (!preview || state === STATE_CONFIRMING || state === STATE_DONE) return
    // Only lots with a truthy lot_id are transferable. Non-lot rows live in
    // `skipped`, but guard defensively against any tuple missing a lot_id.
    // warehouse_location_id must ride along or the backend selects the lot's LOST
    // units store-wide. REM previews carry none — omit the key, never send null.
    const lotMovements = (preview.tuples || [])
      .filter((tp) => tp.lot_id)
      .map((tp) => ({
        lot_id: tp.lot_id,
        quantity: tp.quantity,
        ...(tp.warehouse_location_id ? { warehouse_location_id: tp.warehouse_location_id } : {}),
      }))
    if (lotMovements.length === 0) {
      setInlineError(t('natura_operations.deposit_transfer.no_transferable_lots'))
      setErrorKind(ERROR_INLINE)
      return
    }
    setState(STATE_CONFIRMING)
    setResult(null)
    setErrorKind(ERROR_NONE)
    setInlineError(null)
    try {
      const payload = {
        // mode tells the backend to apply LOST-only + discard finalize for the
        // non-REM modes (store_location, manual); REM is unchanged.
        mode,
        channel_integration_id: preview.resolved?.channel_integration_id,
        move_type: params.move_type,
        move_reason: params.move_reason,
        source_deposit: params.source_deposit,
        destination_deposit: params.destination_deposit,
        finalize,
        lot_movements: lotMovements,
      }
      const data = await executeDepositTransfer(payload)

      // Batches above the backend's inline threshold come back as
      // { status: "processing", task_id } — the SAP calls run in a worker
      // because they can outlast the 30s request budget. Poll until done.
      if (data?.status === 'processing' && data?.task_id) {
        await pollExecuteTask(data.task_id)
        return
      }

      showResult(data)
    } catch (e) {
      handleError(e)
      setState(STATE_PREVIEW_READY)
    }
  }

  const pollExecuteTask = async taskId => {
    setProgressPct(0)

    for (;;) {
      await sleep(EXECUTE_POLL_INTERVAL_MS)
      // The batch outlives the page: without this the loop keeps polling and
      // setting state after the operator navigated away.
      if (!mountedRef.current) return

      let data
      try {
        data = await fetchExecuteTaskStatus(taskId)
      } catch (e) {
        if (!mountedRef.current) return
        handleError(e)
        setState(STATE_PREVIEW_READY)
        return
      }

      if (!mountedRef.current) return

      if (data?.status === 'processing') {
        setProgressPct(data.progress_percentage || 0)
        continue
      }

      if (data?.status === 'failed') {
        setInlineError(data.message || data.error || t('natura_operations.deposit_transfer.error_generic'))
        setErrorKind(ERROR_INLINE)
        setState(STATE_PREVIEW_READY)
        return
      }

      // completed — same payload shape the inline path returns
      showResult(data)
      return
    }
  }

  const cancel = () => {
    invalidatePreview()
  }

  const handleError = e => {
    const httpStatus = e?.response?.status
    if (httpStatus === 422) {
      const payload = e?.response?.data
      setInlineError(payload?.message || payload?.error || t('natura_operations.deposit_transfer.error_generic'))
      setErrorKind(ERROR_INLINE)
    } else {
      setErrorKind(ERROR_GENERIC)
    }
  }

  const isBusy = state === STATE_PREVIEWING || state === STATE_CONFIRMING

  // Per-mode required-fields completeness check — gates "Gerar preview" so an
  // empty/invalid input can't fire a wasted preview request.
  const inputsComplete = (() => {
    switch (mode) {
      case MODE_REM_ORDER:
        return isNonEmpty(inputs.order_number)
      case MODE_STORE_LOCATION:
        // LOST-only: no status selection — store + location is enough.
        return isNumeric(inputs.store_id) && isNonEmpty(inputs.warehouse_location)
      case MODE_MANUAL:
        return isNumeric(inputs.store_id) && isNonEmpty(inputs.sku) && isNonEmpty(inputs.lot_code) && isPositiveInt(inputs.quantity)
      default:
        return false
    }
  })()

  const previewDisabled = isBusy || !inputsComplete

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold mb-4">{t('natura_operations.deposit_transfer.title')}</h1>

      <section className="bg-gray-50 border border-gray-200 rounded p-4 mb-6">
        <h2 className="font-semibold mb-2">{t('natura_operations.deposit_transfer.instructions_heading')}</h2>
        <ol className="list-decimal pl-6 text-sm space-y-1">
          <li>{t('natura_operations.deposit_transfer.instructions_step1')}</li>
          <li>{t('natura_operations.deposit_transfer.instructions_step2')}</li>
          <li>{t('natura_operations.deposit_transfer.instructions_step3')}</li>
        </ol>
        <p className="mt-3 text-sm text-amber-700">{t('natura_operations.deposit_transfer.warning')}</p>
      </section>

      {optionsLoadFailed && (
        <div role="alert" className="mb-4 p-3 border border-red-200 bg-red-50 rounded text-sm text-red-800">
          {t('natura_operations.deposit_transfer.options_load_failed')}
        </div>
      )}

      {errorKind === ERROR_INLINE && inlineError && (
        <div role="alert" className="mb-4 p-3 border border-red-200 bg-red-50 rounded text-sm text-red-800">
          {inlineError}
        </div>
      )}

      <section className="mb-6">
        <h2 className="font-semibold mb-2">{t('natura_operations.deposit_transfer.step1_heading')}</h2>
        <ModeSelector mode={mode} onChange={handleModeChange} disabled={isBusy} />
        {mode === MODE_REM_ORDER && <RemOrderInput values={inputs} onChange={handleInputsChange} disabled={isBusy} />}
        {mode === MODE_STORE_LOCATION && <StoreLocationInput values={inputs} onChange={handleInputsChange} disabled={isBusy} />}
        {mode === MODE_MANUAL && <ManualInput values={inputs} onChange={handleInputsChange} disabled={isBusy} />}
      </section>

      <section className="mb-6">
        <h2 className="font-semibold mb-2">{t('natura_operations.deposit_transfer.step2_heading')}</h2>
        <MovementParams options={options} values={params} onChange={handleParamsChange} disabled={isBusy} />
      </section>

      <div>
        <button
          type="button"
          onClick={generatePreview}
          disabled={previewDisabled}
          className="px-4 py-2 rounded bg-amber-500 hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed text-white"
        >
          {state === STATE_PREVIEWING ? t('natura_operations.deposit_transfer.generating_preview') : t('natura_operations.deposit_transfer.generate_preview')}
        </button>
      </div>

      {errorKind === ERROR_GENERIC && (
        <div className="mt-6 p-4 border border-red-200 bg-red-50 rounded text-sm text-red-800">
          <p className="mb-2">{t('natura_operations.deposit_transfer.error_generic')}</p>
          <button
            type="button"
            onClick={state === STATE_PREVIEW_READY ? confirm : generatePreview}
            className="px-3 py-1.5 text-sm rounded bg-red-600 hover:bg-red-700 text-white"
          >
            {t('natura_operations.deposit_transfer.retry')}
          </button>
        </div>
      )}

      {(state === STATE_PREVIEW_READY || state === STATE_CONFIRMING || state === STATE_DONE) && preview && (
        <PreviewTable
          preview={preview}
          finalize={finalize}
          onFinalizeChange={setFinalize}
          onConfirm={confirm}
          onCancel={cancel}
          confirming={state === STATE_CONFIRMING}
          submitted={state === STATE_DONE}
        />
      )}

      {state === STATE_CONFIRMING && progressPct !== null && (
        <div className="mt-4 bg-gray-50 border border-gray-200 rounded p-4">
          <p className="mb-2 text-sm">{t('natura_operations.deposit_transfer.processing_in_background', { percentage: progressPct })}</p>
          <div className="w-full bg-gray-200 rounded h-2">
            <div className="bg-orange-400 h-2 rounded" style={{ width: `${progressPct}%` }} />
          </div>
        </div>
      )}

      {state === STATE_DONE && result && <ResultTable summary={result.summary} results={result.results || []} />}
    </div>
  )
}
